import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { Col, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { editSection, getAllPageSections, getSection } from "store/actions";
import Loader from "components/shared/Loader";
import { Input, Textarea } from "components/shared/FormComponents";
import SaveChangesButton from "components/shared/SaveChangesButton";
import { toastErrorMessage } from "helpers/toaster/toastErrorMessage";
import "flatpickr/dist/flatpickr.css";
import ReactSelect from "react-select";
import ImagesUpload from "components/shared/ImagesUpload";
import { v4 } from "uuid";
import PdfUpload from "components/shared/PdfUpload";

const EditProduct = () => {
  const { formatMessage, locale } = useIntl();
  const dispatch = useDispatch();
  const { id } = useParams();
  const {
    loading,
    error,
    section,
    sections: categories,
  } = useSelector((state) => state.section);

  const navigate = useNavigate();

  const [slider, setSlider] = useState([]);
  const [selectedPdfFile, setSelectedPdfFile] = useState({
    preview: null,
    path: "",
  });
  // const categoryDefaultValue = categories?.find(
  //   (category) => category?.slug === section?.category
  // )?.heading?.[locale];

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm();

  // effects
  useEffect(() => {
    dispatch(getSection({ page: "products-1", slug: id }));
    dispatch(getAllPageSections("productsCategories"));
  }, [dispatch, id]);

  useEffect(() => {
    reset(section);

    let images = [];
    section?.images?.forEach((imagePath) => {
      const image = {
        id: v4(),
        preview: imagePath,
        path: imagePath,
      };
      images.push(image);
    });
    if (section?.pdfFile) {
      setSelectedPdfFile({
        preview: section?.pdfFile,
        path: section?.pdfFile,
      });
    }
    setSlider(images);
  }, [reset, section]);

  // error
  useEffect(() => {
    if (error) {
      toastErrorMessage(formatMessage({ id: "sendingErrorMessage" }), locale);
    }
  }, [error, formatMessage, locale]);

  const onSubmit = (data) => {
    if (slider?.length > 0) {
      data.images = slider.map((image) => {
        return image.path;
      });
    }

    if (selectedPdfFile?.path) {
      data.pdfFile = `/uploads/${selectedPdfFile?.path?.split("/").pop()}`;
    } else {
      data.pdfFile = null;
    }

    dispatch(
      editSection({
        slug: id,
        page: "products-1",
        data: { section: data },
        navigate: () => navigate("/products"),
      })
    );
  };

  if (loading) return <Loader />;

  return (
    <>
      <div className="acc-form">
        <div className="card">
          <div className="card-head">
            <h4>
              <FormattedMessage id={"editItem"} />
            </h4>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Col xl={8} md={12}>
                <Row>
                  <Col lg={12} xs={12}>
                    <div className="form-group ">
                      <h5>
                        <FormattedMessage id={"images"} />
                      </h5>
                      <ImagesUpload
                        imagesURLs={slider}
                        setImageURLs={setSlider}
                      />
                    </div>
                  </Col>
                  <Col lg={6} xs={12}>
                    <div className="form-group required">
                      <h5>
                        <FormattedMessage id={"pdfFile"} />
                      </h5>
                      <PdfUpload
                        selectedPdfFile={selectedPdfFile}
                        setSelectedPdfFile={setSelectedPdfFile}
                      />
                    </div>
                  </Col>
                  <Row />
                  <Col xs={12}>
                    <div className="form-group ">
                      <h5>
                        <FormattedMessage id="category" />
                      </h5>

                      <Controller
                        control={control}
                        name="category"
                        rules={{ required: true }}
                        render={({ field: { onChange, value, name, ref } }) => (
                          <ReactSelect
                            inputRef={ref}
                            name={name}
                            placeholder={<FormattedMessage id={"category"} />}
                            options={categories?.map((c) => ({
                              value: c?.slug,
                              label: c?.heading?.[locale],
                            }))}
                            onChange={(selectedOption) => {
                              onChange(selectedOption.value);
                            }}
                            isRtl={true}
                            isSearchable={false}
                            className="basic-single"
                            classNamePrefix="select"
                          />
                        )}
                      />
                    </div>
                  </Col>

                  <Col lg={6} xs={12}>
                    <Input
                      langId="name"
                      name="name.ar"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="name"
                      name="name.en"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Textarea
                      type="text"
                      langId="description"
                      name="description.ar"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Textarea
                      type="text"
                      langId="description"
                      name="description.en"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="capacity"
                      name="capacity.ar"
                      register={register}
                      rules={{ required: false }}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="capacity"
                      name="capacity.en"
                      register={register}
                      rules={{ required: false }}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="space"
                      name="space.ar"
                      register={register}
                      rules={{ required: false }}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="space"
                      name="space.en"
                      register={register}
                      rules={{ required: false }}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="numberFloors"
                      name="numberFloors.ar"
                      register={register}
                      rules={{ required: false }}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="numberFloors"
                      name="numberFloors.en"
                      register={register}
                      rules={{ required: false }}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="category"
                      name="categoryTitle.ar"
                      register={register}
                      rules={{ required: false }}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="category"
                      name="categoryTitle.en"
                      register={register}
                      rules={{ required: false }}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="doors"
                      name="doors.ar"
                      register={register}
                      rules={{ required: false }}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="doors"
                      name="doors.en"
                      register={register}
                      rules={{ required: false }}
                      errors={errors}
                    />
                  </Col>
                </Row>

                <SaveChangesButton />
              </Col>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditProduct;
